import { Flex, theme } from '../../2022-component-library';
import { SessionShell, SessionHeader } from '../../ui';
import { useSessionState } from '../../context/sessionStateContext';
import { ScreenSizeGuard } from '../session/ScreenSizeGuard';
import { buildMenuItems, useConfirmExitSession } from '../session/hooks';
import { usePatientNotes } from '../session/PatientNotes';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { useMediaQuery } from '../../helpers/useMediaQuery';
import { useState, useEffect, useLayoutEffect } from 'react';
import { useTestContext } from '../../context/testContext';
import { useCalibration } from '../../context/calibrationContext';
import { useVideo } from '../../context/videoContext';
import { useAppointment } from '../../context/appointmentContext';
import { calibrationConstants } from '../../helpers/calibration-constants';
import { ResponsiveContentPatient, ResponsiveRotateScreen } from '.';
import { useNavigationSync } from '../../context/navigationContext';
import { useReceiveMessage, usePostMessage } from '../../context/messageContext';
import { useRestorePatientTab } from '../session/calibration/sync';

const TabletViewContainer = styled(Flex)`
  @media screen and (min-width: 750px) and (min-height: 820px) {
    width: 744px;
    height: 744px;
    justify-content: center;
    border-radius: 4px;
    box-shadow: ${theme.elevations.elevation1};
  }
`;

const ScreenLock = () => {
  const portraitView = useMediaQuery('(max-width: 700px) and (orientation: portrait)');
  return (
    <Flex
      centered
      style={{ position: 'absolute', top: 0, left: 0, zIndex: 900, width: '100vw', height: '100vh', overflow: 'hidden', backgroundColor: '#fff' }}
    >
      <ResponsiveRotateScreen orientation={portraitView ? 'landscape' : 'portrait'} />
    </Flex>
  );
};

const ResponsivePatientContainer = () => {
  let navigate = useNavigate();
  const { updateTestData } = useTestContext();
  const [showScreenLock, setShowScreenLock] = useState(false);
  const { activePatientTab: activeTab, setActivePatientTab: setActiveTab } = useNavigationSync();
  const [logmarData, setLogmarData] = useState({});
  const [logmarType, setLogmarType] = useState();
  const [optimaData, setOptimaData] = useState();
  const [visualFieldTestControl, setVisualFieldTestControl] = useState('stopVisualField');
  const [blindSpotUpdateMessage, setBlindSpotUpdateMessage] = useState(null);
  const { disconnect } = useVideo();
  const { appointment } = useAppointment();
  const receivedMessage = useReceiveMessage();
  const postMessage = usePostMessage();
  const { blindSpotThumbnailHeightPx, blindSpotThumbnailWidthPx, blindSpotThumbnailY } = calibrationConstants;
  const [testInProgress, setTestInProgress] = useState(false);
  const wrapInTabletView = useMediaQuery('(min-width: 700px) and (min-height: 700px)');
  const isPortraitView = useMediaQuery('(max-width: 700px) and (orientation: portrait)');
  const {
    calibrationData: { circleDimensionsPx },
  } = useCalibration();
  const { state: sessionState } = useSessionState();
  const sessionStep = sessionState?.step;
  const exitSession = useConfirmExitSession();
  const notes = usePatientNotes();
  // Re-joining patient: land on the clinician's current calibration / distance screen.
  useRestorePatientTab(activeTab, setActiveTab);

  useLayoutEffect(() => window.scrollTo(0, 0), [activeTab]);

  useEffect(() => {
    const testTabs = [
      'logmar',
      'randomE',
      'optimaChart',
      'contrastSensitivity',
      'refraction',
      'kaysOptotypes',
      'kaysMultiple',
      'visualField',
      'visualFieldForthValley',
      'refractionTwo',
      'ishihara',
    ];
    // Full-bleed only while a test stimulus is on screen; back on 'holding' (P7) the header returns.
    setTestInProgress(testTabs.includes(activeTab));
  }, [activeTab]);

  useEffect(() => {
    const landscapeTabs = ['kaysMultiple', 'logmar', 'randomE', 'contrastSensitivity', 'optimaChart'];
    const shouldBePortraitView = activeTab === 'refractionTwo';
    const shouldBeLandscapeView = landscapeTabs.includes(activeTab);

    const checkForScreenLock = () => {
      if (isPortraitView && shouldBeLandscapeView) return setShowScreenLock(true);
      if (!isPortraitView && shouldBePortraitView) return setShowScreenLock(true);
      return setShowScreenLock(false);
    };

    checkForScreenLock();
  }, [isPortraitView, activeTab]);

  const blindSpotDefaultValues = {
    blindSpotVideo: {
      clientX: window.screen.availWidth / 2 - blindSpotThumbnailWidthPx / 2,
      clientY: blindSpotThumbnailY,
      circleDimensionsPx: circleDimensionsPx,
    },
    blindSpot: {
      clientX: window.screen.availWidth / 2 - circleDimensionsPx / 2,
      clientY: blindSpotThumbnailY + blindSpotThumbnailHeightPx / 2,
      circleDimensionsPx: circleDimensionsPx,
      show: false,
    },
  };

  const [blindSpotData, setBlindSpotData] = useState(blindSpotDefaultValues);

  const movementNotification = (messageContent) => {
    postMessage(messageContent);
  };

  const testNotification = (messageContent) => {
    const message = {
      type: 'visualFieldUpdate',
      content: messageContent,
    };
    postMessage(message);
  };

  useEffect(() => {
    switch (receivedMessage?.type) {
      case 'navigateToWelcomeScreen':
        return navigate(appointment?.currentAppointment?.room_name);
      case 'screen-calibration':
        setActiveTab('screen-calibration');
        break;
      case 'camera-calibration':
        setActiveTab('camera-calibration');
        break;
      case 'stepDeviceCalibration':
        setActiveTab('screen-calibration'); // P3.1 Make / Device pickers
        break;
      case 'stepCardCalibration':
        setActiveTab('card');
        break;
      case 'stepRulerCalibration':
        setActiveTab('ruler');
        break;
      case 'blindspot':
        setBlindSpotData(blindSpotDefaultValues);
        break;
      case 'blindSpotXPosition':
        setBlindSpotUpdateMessage({ type: 'blindSpotXPosition', msgBody: receivedMessage?.content });
        break;
      case 'blindSpotVideoXPosition':
        setBlindSpotUpdateMessage({ type: 'blindSpotVideoXPosition', msgBody: receivedMessage?.content });
        break;
      case 'visualAcuityTest':
        setLogmarData({
          size: receivedMessage?.content.fontSize,
          distance: receivedMessage?.content.distanceFromScreen,
          characters: receivedMessage?.content.logmarCharacters,
        });
        setLogmarType(receivedMessage?.content.logmarType);
        setActiveTab('logmar');
        break;
      case 'resizeLogmar':
        setLogmarData({
          size: receivedMessage?.content.fontSize,
          distance: receivedMessage?.content.distanceReferenceInMM,
          characters: receivedMessage?.content.logmarCharacters,
        });
        break;
      case 'resizeLogmarImage':
        setLogmarData({
          size: receivedMessage?.content.contentSize,
          distance: receivedMessage?.content.distanceReferenceInMM,
          logmarContent: receivedMessage?.content.logmarContent,
        });
        break;
      case 'optimaTextContents':
        setOptimaData({
          textContents: receivedMessage?.content.textContents.fontSizes,
        });
        break;
      case 'randomE':
        setActiveTab('randomE');
        break;
      case 'kaysOptotypes':
        setActiveTab('kaysOptotypes');
        break;
      case 'optimaChart':
        setActiveTab('optimaChart');
        break;
      case 'peleRobsonTest':
        setActiveTab('peleRobson');
        break;
      case 'visualField':
        setActiveTab('visualField');
        break;
      case 'heartBeat':
        postMessage({ type: 'heartBeatResponse', room_name: receivedMessage?.room_name });
        break;
      case 'opticianLoggedOut':
        disconnect();
        navigate(`${process.env.PUBLIC_URL}/error`, {
          state: { error: 'This session has been closed. Close your browser to end the appointment.', title: 'Session closed' },
        });
        break;
      case 'contrastSensitivity':
        setActiveTab('contrastSensitivity');
        setLogmarData({
          size: receivedMessage?.content.fontSize,
          distance: receivedMessage?.content.distanceFromScreen,
          backgroundColor: receivedMessage?.content.backgroundColor,
          fontColor: receivedMessage?.content.fontColor,
          characters: receivedMessage?.content.characters,
        });
        break;
      case 'updateContrastSensitivityContent':
        const { backgroundColor, fontColor, characters } = receivedMessage?.content;
        setLogmarData((existingData) => {
          return { ...existingData, backgroundColor: backgroundColor, fontColor: fontColor, characters: characters };
        });
        updateTestData({ backgroundColor: backgroundColor, fontColor: fontColor, characters: characters });

        break;
      case 'startVisualField':
        setVisualFieldTestControl({
          command: 'startVisualField',
          visualFieldTestData: receivedMessage?.content,
        });
        break;
      case 'stopVisualField':
        setVisualFieldTestControl({
          command: 'stopVisualField',
          visualFieldTestData: receivedMessage?.content,
        });
        break;
      case 'initColors':
        setVisualFieldTestControl({
          command: 'initColors',
          visualFieldTestData: receivedMessage?.content,
        });
        break;
      case 'reloadTest':
        setVisualFieldTestControl({
          command: 'reloadTest',
          visualFieldTestData: receivedMessage?.content,
        });
        break;
      case 'visualFieldForthValley':
        return setActiveTab('visualFieldForthValley');
      case 'startRefraction':
        return setActiveTab('refraction');
      case 'startRefractionTwo':
        return setActiveTab('refractionTwo');
      case 'cardCalibration':
        return setActiveTab(receivedMessage?.content.screen);
      case 'kaysMultiple':
        return setActiveTab('kaysMultiple');
      case 'ishiharaPlate':
        return setActiveTab('ishihara');
      default:
        break;
    }
  }, [receivedMessage]);

  const renderResponsivePatientContent = () => {
    return (
      <ResponsiveContentPatient
        activeTab={activeTab}
        calibrationStep={activeTab}
        setActiveTab={setActiveTab}
        blindSpotData={blindSpotData}
        setBlindSpotData={setBlindSpotData}
        movementNotification={movementNotification}
        logmarData={logmarData}
        logmarType={logmarType}
        optimaData={optimaData}
        visualFieldTestControl={visualFieldTestControl}
        testNotification={testNotification}
        blindSpotUpdateMessage={blindSpotUpdateMessage}
        setBlindSpotUpdateMessage={setBlindSpotUpdateMessage}
      />
    );
  };

  // Every non-test tab (waiting P2/P7, calibration P3-P4, distance P5-P6) renders in the IBISengage
  // SessionLayout (screens/session/calibration). No tab keeps the legacy tablet box any more, and
  // none of them renders its own <LocalVideo/>: the strip's local tile is the only local video.
  const legacyTabs = [];
  const waiting = !legacyTabs.includes(activeTab);

  if (testInProgress) {
    return (
      <ScreenSizeGuard>
        {showScreenLock && <ScreenLock />}
        <Flex justifyContent='center' width='100vw' height={`${window.innerHeight}px`} maxWidth={showScreenLock && '10vw'} maxHeight={showScreenLock && '10vh'}>
          {renderResponsivePatientContent()}
        </Flex>
      </ScreenSizeGuard>
    );
  }

  return (
    <ScreenSizeGuard>
      {showScreenLock && <ScreenLock />}
      <SessionShell
        fixed={waiting}
        padding={waiting ? undefined : '0'}
        header={
          <SessionHeader
            title={patientTitle(activeTab, sessionStep)}
            onSeeNotes={notes.onSeeNotes}
            menuItems={buildMenuItems({ activeTab: menuStep(activeTab, sessionStep), readOnly: true, onEnd: exitSession, endLabel: 'Exit Session' })}
          />
        }
      >
        {waiting ? (
          renderResponsivePatientContent()
        ) : (
          <Flex justifyContent='center' alignItems={wrapInTabletView ? 'center' : null} width='100%' flex='1'>
            {activeTab !== 'blindspot' ? <TabletViewContainer>{renderResponsivePatientContent()}</TabletViewContainer> : renderResponsivePatientContent()}
          </Flex>
        )}
      </SessionShell>
      {notes.drawer}
    </ScreenSizeGuard>
  );
};

// Header title per patient step (Figma P2-P7).
const CALIBRATION_TABS = ['screen-calibration', 'camera-calibration', 'ruler', 'card'];
const patientTitle = (tab, step) => {
  if (CALIBRATION_TABS.includes(tab)) return tab === 'camera-calibration' ? 'Camera Calibration' : 'Calibration Selection';
  if (tab === 'pupillaryDistance' || tab === 'blindspot') return 'Set Distance';
  if (step === 'set-distance') return 'Set Distance';
  if (!step || step === 'calibration-selection') return 'Calibration Selection';
  if (step === 'results') return 'Completed Examinations';
  return 'Examinations';
};
const menuStep = (tab, step) => {
  if (CALIBRATION_TABS.includes(tab)) return 'calibration-selection';
  if (tab === 'pupillaryDistance' || tab === 'blindspot') return 'set-distance';
  if (['calibration-selection', 'set-distance', 'results'].includes(step)) return step;
  return step ? 'test' : 'calibration-selection';
};

export default ResponsivePatientContainer;
